import {Button, MenuItem, Paper, TextField} from '@material-ui/core';
import {getNonEmptyStringErrors} from 'Core/models/EmptyString';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import * as O from 'fp-ts/lib/Option';
import AddressChangeRequestForm from 'Agent/Policies/components/AddressChangeRequest';
import DirectBillingChangeRequest from 'Agent/Policies/components/DirectBillingChangeRequest';
import PayrollAllotmentBillingChanges from 'Agent/Policies/components/PayrollAllotmentBillingChanges';
import {
  addAdditionalPolicyNumber,
  changeAdditionalPolicies,
  changeInsuredFirstName,
  changeInsuredLastName,
  changeInsuredMiddleName,
  changePolicyNumber,
  changeRequestType,
  clear,
  removeAdditionalPolicyNumber,
  savePolicyServiceRequest,
  submitInitialRequest,
} from 'Agent/Policies/ducks/policyServiceRequest';
import {matchChangeRequest} from 'Agent/Policies/models/ServiceRequest/ChangeRequest';
import {
  ChangeType,
  InitialPolicyServiceRequest,
  isValidInitialPolicyServiceRequest,
  PolicyNumber,
  ValidInitialPolicyServiceRequest,
} from 'Agent/Policies/models/ServiceRequest/InitialPolicyServiceRequest';
import {matchPolicyServiceRequest} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequest';
import {
  isValidPolicyServiceRequest,
  PolicyServiceRequestWithChangeRequest,
} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequestWithChangeRequest';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {compose} from 'redux';
import {ReducerState} from 'rootReducer';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {withEventValue} from 'Shared/utilities/WithEventValue';
import './PolicyServiceRequest.sass';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';

type IntialPolicyServiceRequestFormProps = {
  serviceRequest: InitialPolicyServiceRequest;
};

const InitialPolicyServiceRequestForm = (props: IntialPolicyServiceRequestFormProps) => {
  const dispatch = useDispatch();
  const {serviceRequest} = props;
  const match = useRouteMatch();
  useBreadcrumbs('Policy Service Request', match.url);
  const AdditionalPolicyNumbers = (serviceRequest.additionalPolicyNumbers as PolicyNumber[]).map((policyNum, index) => (
    <div key={policyNum.id}>
      <ValidatableTextField
        className="service-request-form__field"
        errors={getNonEmptyStringErrors(serviceRequest.policyNumber)}
        label={`Additional Policy ${index + 1}`}
        value={policyNum.value.value}
        onChange={value => dispatch(changeAdditionalPolicies(policyNum.id, value))}
        inputProps={{
          maxLength: 10,
        }}
      />
      <Button onClick={() => dispatch(removeAdditionalPolicyNumber(policyNum.id))}>Remove</Button>
    </div>
  ));

  return (
    <form className="policy-service-request__form">
      <h1>Change Policy Service Request</h1>
      <p>Please fill out the fields below to submit a service request.</p>
      <div className="policy-service-request__policy-numbers">
        <p>Policy Number(s)</p>
        <ValidatableTextField
          label="Policy Number"
          className="service-request-form__field"
          errors={getNonEmptyStringErrors(serviceRequest.policyNumber)}
          onChange={compose(dispatch, changePolicyNumber)}
          inputProps={{
            maxLength: 10,
          }}
          value={serviceRequest.policyNumber.value}
        />
        {AdditionalPolicyNumbers}
        <Button onClick={() => dispatch(addAdditionalPolicyNumber())}>
          Add&nbsp;
          <i className="fa fa-plus" />
        </Button>
      </div>
      <div className="service-request-form__section">
        <strong className="service-request-form__subheader">Policy Information</strong>
        <ValidatableTextField
          label="Insured First Name"
          className="service-request-form__field"
          errors={getNonEmptyStringErrors(serviceRequest.insuredFirstName)}
          onChange={compose(dispatch, changeInsuredFirstName)}
          value={serviceRequest.insuredFirstName.value}
        />
        <TextField
          label="Insured Middle Name"
          className="service-request-form__field"
          variant="outlined"
          onChange={withEventValue(compose(dispatch, changeInsuredMiddleName))}
          value={serviceRequest.insuredMiddleName.value}
        />
        <ValidatableTextField
          label="Insured Last Name"
          className="service-request-form__field"
          errors={getNonEmptyStringErrors(serviceRequest.insuredLastName)}
          onChange={compose(dispatch, changeInsuredLastName)}
          value={serviceRequest.insuredLastName.value}
        />
        <OutlinedSelect
          label="Service Request Change Type"
          className="service-request-form__request-change-type-select"
          fullWidth
          value={O.isSome(serviceRequest.requestChangeType) ? serviceRequest.requestChangeType.value : ''}
          onChange={e => dispatch(changeRequestType(parseInt(e.target.value)))}
        >
          <MenuItem value={ChangeType.AddressChange}>Address</MenuItem>
          <MenuItem value={ChangeType.DirectBillingChange}>Direct Billing</MenuItem>
          <MenuItem value={ChangeType.BillingScheduleChange}>Billing Schedule</MenuItem>
        </OutlinedSelect>
      </div>
      <div>
        <Link to={CoreRouteConstants.DASHBOARD}>
          <Button className="service-request-form__cancel-button" variant="contained">
            Cancel
          </Button>
        </Link>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValidInitialPolicyServiceRequest(serviceRequest)}
          onClick={e => {
            if (isValidInitialPolicyServiceRequest(serviceRequest)) {
              dispatch(submitInitialRequest(serviceRequest));
            }
          }}
        >
          Next
        </Button>
      </div>
    </form>
  );
};

type PolicyServiceRequestHeaderProps = {
  validInitialServiceRequest: ValidInitialPolicyServiceRequest;
};

const PolicyServiceRequestHeader = (props: PolicyServiceRequestHeaderProps) => {
  return (
    <div className="policy-service-request__inital-request-header">
      <span>
        <strong>Policy Number:</strong> {props.validInitialServiceRequest.policyNumber.value}
      </span>
      <span>
        <strong>Insured Name:</strong>{' '}
        {`${props.validInitialServiceRequest.insuredLastName.value}, ${props.validInitialServiceRequest.insuredFirstName.value} ${props.validInitialServiceRequest.insuredMiddleName.value}`}
      </span>
      <span>
        {props.validInitialServiceRequest.additionalPolicyNumbers.length > 0 && (
          <>
            <strong>Additional Policies:</strong>{' '}
            {props.validInitialServiceRequest.additionalPolicyNumbers
              .map(policyNum => policyNum.value.value)
              .join(', ')}
          </>
        )}
      </span>
    </div>
  );
};

type PolicyServiceRequestFormWithChangeRequestProps = {
  serviceRequest: PolicyServiceRequestWithChangeRequest;
} & WithSelectedAgencyProps;

const PolicyServiceRequestFormWithChangeRequest = (props: PolicyServiceRequestFormWithChangeRequestProps) => {
  const {selectedAgency, serviceRequest} = props;
  const dispatch = useDispatch();

  return (
    <div className="service-request-form__change-request">
      {matchChangeRequest(serviceRequest.changeRequest, {
        AddressChangeRequest: address => <h1>Address Change Request</h1>,
        DirectBillingChangeRequest: directBilling => <h1>Direct Billing Change Request</h1>,
        PayrollAllotmentChangeRequest: payroll => <h1>Payroll Allotment Change Request</h1>,
      })}
      <PolicyServiceRequestHeader validInitialServiceRequest={serviceRequest.initialPolicyServiceRequestInformation} />
      {matchChangeRequest(serviceRequest.changeRequest, {
        AddressChangeRequest: address => <AddressChangeRequestForm addressChangeRequest={address} />,
        DirectBillingChangeRequest: directBilling => (
          <DirectBillingChangeRequest directBillingChangeRequest={directBilling} />
        ),
        PayrollAllotmentChangeRequest: payroll => (
          <PayrollAllotmentBillingChanges payrollAllotmentBillingChanges={payroll} />
        ),
      })}
      <Link to={CoreRouteConstants.DASHBOARD}>
        <Button className="service-request-form__cancel-button" variant="contained">
          Cancel
        </Button>
      </Link>
      <Button
        variant="contained"
        color="primary"
        disabled={!isValidPolicyServiceRequest(serviceRequest)}
        onClick={e => {
          if (isValidPolicyServiceRequest(serviceRequest)) {
            dispatch(savePolicyServiceRequest(selectedAgency.encryptedIdentifier, serviceRequest));
          }
        }}
      >
        Submit
      </Button>
    </div>
  );
};

const PolicyServiceRequest = (props: WithSelectedAgencyProps) => {
  const {serviceRequest} = useSelector((state: ReducerState) => state.policyServiceRequest);
  const {selectedAgency} = props;
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(clear());
    };
  }, []);

  return (
    <div className="policy-service-request">
      <Paper className="policy-service-request__form-wrapper">
        {matchPolicyServiceRequest(serviceRequest, {
          Initial: initial => <InitialPolicyServiceRequestForm serviceRequest={initial} />,
          WithChangeRequest: withChangeRequest => (
            <>
              <PolicyServiceRequestFormWithChangeRequest
                selectedAgency={selectedAgency}
                serviceRequest={withChangeRequest}
              />
            </>
          ),
        })}
      </Paper>
    </div>
  );
};

export default withSelectedAgency(PolicyServiceRequest);
